/**
 * WapHeroCarousel — Custom element para o hero carrossel da home
 * Section: wap-hero-home
 * Ref: Figma node 1095:127 (nav prev/next, 56×56px, bottom-right)
 */

class WapHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slides = [];
    this.dots = [];
    this.total = 0;
    this._autoplayTimer = null;
    this._autoplayDelay = parseInt(this.dataset.autoplay || '6000', 10);
    this._isAnimating = false;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.wap-hero-home__slide'));
    this.total = this.slides.length;

    if (this.total === 0) return;

    this._buildDots();
    this._goTo(0, false);

    if (this.total === 1) return;

    this.querySelector('.wap-hero-home__prev')
      ?.addEventListener('click', () => { this._stopAutoplay(); this._prev(); this._startAutoplay(); });

    this.querySelector('.wap-hero-home__next')
      ?.addEventListener('click', () => { this._stopAutoplay(); this._next(); this._startAutoplay(); });

    /* Pausa autoplay ao passar o mouse */
    this.addEventListener('mouseenter', () => this._stopAutoplay());
    this.addEventListener('mouseleave', () => this._startAutoplay());

    /* Pausa quando fora do viewport (performance) */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries[0].isIntersecting ? this._startAutoplay() : this._stopAutoplay();
      }, { threshold: 0.2 });
      io.observe(this);
    } else {
      this._startAutoplay();
    }

    /* Suporte a swipe em touch */
    this._initTouch();
  }

  disconnectedCallback() {
    this._stopAutoplay();
  }

  _goTo(index, animate = true) {
    if (this._isAnimating && animate) return;

    const prev = this.currentIndex;
    this.currentIndex = ((index % this.total) + this.total) % this.total;

    if (animate) {
      this._isAnimating = true;
      setTimeout(() => { this._isAnimating = false; }, 700);
    }

    this.slides[prev]?.classList.remove('is-active');
    this.slides[this.currentIndex]?.classList.add('is-active');

    this._updateDots();
    this._updateAriaLabels();
  }

  _prev() { this._goTo(this.currentIndex - 1); }
  _next() { this._goTo(this.currentIndex + 1); }

  _startAutoplay() {
    if (this.total <= 1) return;
    this._stopAutoplay();
    this._autoplayTimer = setInterval(() => this._next(), this._autoplayDelay);
  }

  _stopAutoplay() {
    clearInterval(this._autoplayTimer);
    this._autoplayTimer = null;
  }

  _buildDots() {
    const container = this.querySelector('.wap-hero-home__dots');
    if (!container || this.total <= 1) {
      container?.remove();
      return;
    }

    this.slides.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.className = 'wap-hero-home__dot';
      btn.setAttribute('aria-label', `Ir para slide ${i + 1} de ${this.total}`);
      btn.addEventListener('click', () => {
        this._stopAutoplay();
        this._goTo(i);
        this._startAutoplay();
      });
      container.appendChild(btn);
    });

    this.dots = Array.from(container.querySelectorAll('.wap-hero-home__dot'));
  }

  _updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');
    });
  }

  _updateAriaLabels() {
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== this.currentIndex ? 'true' : 'false');
    });
  }

  _initTouch() {
    let startX = 0;
    let startY = 0;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;

      /* Só ativa se movimento horizontal > vertical e > 40px */
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        this._stopAutoplay();
        dx < 0 ? this._next() : this._prev();
        this._startAutoplay();
      }
    }, { passive: true });
  }
}

if (!customElements.get('wap-hero-carousel')) {
  customElements.define('wap-hero-carousel', WapHeroCarousel);
}
